/*
 * Part Table Container
 */

import { connect } from 'react-redux';
import { IinitialState } from '../../reducers';
import PartTable from './PartTable';
import { withTranslation, WithTranslation } from 'react-i18next';
import { constants } from '../../constants/constants';
import { updateWorkOrder } from '../../actions/workOrderActions';

import { selectIsLoading } from '../../reducers/commonReducers';
import { selectIsMPFormLocked } from '../../reducers/commonSelectors';
import {
    clearSelectedInstallBasePartID,
    deleteInstallBasePart,
    setSelectedInstallBasePartID
} from '../../actions/partsActions';
import {
    selectAllInstallBasePartsForInstallBaseAndJob,
    selectJobPartsForSelectedJob
} from '../../reducers/partsReducer';

interface Iprops {
    colorButton?: string;
    installID: string;
}

const mapStateToProps = (state: IinitialState, ownProps: Iprops & WithTranslation) => {
    const { selectedInstallBasePartID } = state.parts;
    const allTableData = selectAllInstallBasePartsForInstallBaseAndJob(state, {
        installBaseID: ownProps.installID
    });

    // Additional filtering: Remove install base parts that reference deleted work order parts
    const filteredTableData = allTableData.filter(installBasePart => {
        if (installBasePart.workOrderPartID) {
            const workOrderPart = state.parts.workOrderPartsByID[installBasePart.workOrderPartID];
            // Only include if work order part exists and is not deleted
            return workOrderPart && workOrderPart.isDeleted === false;
        }
        // For non-work-order parts, include them
        return true;
    });

    // Sort table data: work order parts, job parts, boot stock, then by description
    const tableData = filteredTableData.sort((a, b) => {
        // Determine part type priority (lower number = higher priority)
        const getTypePriority = (item: any) => {
            if (item.workOrderPartID) return 1; // work order parts
            if (item.partStockID) return 3; // boot stock parts
            return 2; // job parts (or direct parts)
        };

        const aPriority = getTypePriority(a);
        const bPriority = getTypePriority(b);

        // First sort by type priority
        if (aPriority !== bPriority) {
            return aPriority - bPriority;
        }

        // For work order parts, sort by work order number before description
        if (aPriority === 1 && bPriority === 1) {
            // Both are work order parts
            const aWorkOrderPart = a.workOrderPartID ? state.parts.workOrderPartsByID[a.workOrderPartID] : null;
            const bWorkOrderPart = b.workOrderPartID ? state.parts.workOrderPartsByID[b.workOrderPartID] : null;

            if (aWorkOrderPart && bWorkOrderPart) {
                const aWorkOrder = state.workOrder.workOrdersByID[aWorkOrderPart.workOrderID];
                const bWorkOrder = state.workOrder.workOrdersByID[bWorkOrderPart.workOrderID];

                const aWorkOrderNumber = aWorkOrder?.number || '';
                const bWorkOrderNumber = bWorkOrder?.number || '';

                // Sort by work order number first
                const workOrderCompare = aWorkOrderNumber.localeCompare(bWorkOrderNumber);
                if (workOrderCompare !== 0) {
                    return workOrderCompare;
                }
            }
        }

        // Then sort alphabetically by part description (for all types, and as fallback for work order parts)
        const aDescription = a.part?.description || '';
        const bDescription = b.part?.description || '';
        return aDescription.localeCompare(bDescription);
    });

    const isUS = state.user.countryID === constants.usCountryID;
    const disabled = selectIsMPFormLocked(state);

    // Get job parts and work order parts for estimated quantities
    const jobParts = selectJobPartsForSelectedJob(state);
    const workOrderParts = state.parts.workOrderPartsByID;
    const workOrders = state.workOrder.workOrdersByID;

    return {
        loading: selectIsLoading(state),
        tableData,
        isUS,
        currentTile: constants.getTileByURL(window.location.pathname),
        selectedInstallBasePartID,
        disabled,
        selectedJob: state.manageJob.selectedJob,
        jobParts,
        workOrderParts,
        workOrders
    };
};

export const PartTableContainer = withTranslation('manageWorkOrder')(
    connect(mapStateToProps, {
        deleteInstallBasePart,
        updateWorkOrder,
        setSelectedInstallBasePartID,
        clearSelectedInstallBasePartID
    })(PartTable)
);
